import debugModule from 'debug';
import { deleteCookie, getCookie, putCookie } from '@collectivehealth/cookie';
import Events from './events';
import Request from './request';
import Sentry from './sentry';
import { cleanToken, decodeToken, isTokenExpired, isTokenValid } from '../helpers/jwt';
import { SESSION_COOKIE_KEY_PREFIX } from '../etc/constants';
var debug = debugModule('frontend-cog:session');
/**
 * @private
 *
 * Use secure cookies when using HTTPS.
 *
 * @type {boolean}
 */

var USE_SECURE_COOKIES = window.location.protocol === 'https:';
/**
 * @private
 *
 * "Options" parameter passed to putCookie/deleteCookie.
 *
 * @type {object}
 */

var COOKIE_OPTIONS = {
  domain: window.location.hostname,
  secure: USE_SECURE_COOKIES,
  samesite: 'lax'
};
/**
 * @private
 *
 * Key used to persist session tokens in cookies.
 *
 * @type {string}
 */

export var COOKIE_KEY = "".concat(SESSION_COOKIE_KEY_PREFIX).concat(window.location.hostname);
/**
 * An encoded JWT consists of three base64-encoded parts separated by periods.
 * See more information at {@link https://jwt.io/}.
 *
 * @typedef {string} Session~EncodedToken
 * @memberof Session
 */

/**
 * A decoded JWT is an object with information about the user.
 *
 * @typedef {object} Session~DecodedToken
 * @memberof Session
 *
 * @property {number} id - ID of the logged-in user.
 * @property {number} exp - Expiration time of the token as **seconds** since
 * the epoch. To get the date in JS, multiply by 1000. e.g. `new
 * Date(session.exp * 1000)`.
 * @property {number} escalatedGroupExpiry - Expiration time of the escalated
 * user session as **seconds** since the epoch. To get the date in JS, multiply
 * by 1000. e.g. `new Date(session.escalatedGroupExpiry * 1000)`.
 * @property {number} loggedInAt - Logged in timestamp of the token as
 * **seconds** since the epoch. To get the date in JS, multiply by 1000. e.g.
 * `new Date(session.loggedInAt * 1000)`.
 * @property {string} email - Email address of the logged-in user.
 * @property {string} role - Role of the logged-in user.
 * @property {string} poserRole - Role of the user which created this token by
 * posing.
 * @property {number} poserUid - ID of the user which created this token by
 * posing.
 */

/**
 * Attempts to get the encoded JWT from cookies. If found, ensures the token is
 * valid and not expired, then returns it. Will return false if the token is
 * invalid or expired.
 *
 * @memberof Session
 *
 * @return {Session~EncodedToken|boolean}
 */

function getSessionToken() {
  var token = cleanToken(getCookie(COOKIE_KEY));

  if (!token) {
    // There was no token in cookies.
    return false;
  }

  if (!isTokenValid(token)) {
    // Token is truthy but invalid (possibly tampered-with).
    debug("Cookie contained invalid token: \"".concat(token, "\""));
    deleteCookie(COOKIE_KEY, COOKIE_OPTIONS);
    return false;
  }

  if (isTokenExpired(token)) {
    // Token was truthy and valid, but expired.
    debug('Cookie contained an expired token.');
    deleteCookie(COOKIE_KEY, COOKIE_OPTIONS);
    return false;
  } // Token is valid and not expired.


  return token;
}
/**
 * Attempts to get the encoded JWT from cookies. If found, ensures the token is
 * valid and not expired, then returns the decoded token. Will return false if
 * the token is invalid, expired, or cannot be decoded.
 *
 * @memberof Session
 *
 * @return {Session~DecodedToken|boolean}
 */


function getSession() {
  var token = getSessionToken();

  if (!token) {
    return false;
  }

  return decodeToken(token);
}
/**
 * Returns true if there is a valid and unexpired JWT present in cookies.
 *
 * @memberof Session
 *
 * @return {boolean}
 */


function hasSession() {
  return Boolean(getSessionToken());
}
/**
 * Begin a user session with the given JWT.
 *
 * @memberof Session
 *
 * @param {Session~EncodedToken} token - JWT extracted from an API call, local
 * storage or a cookie.
 * @return {Session~DecodedToken}
 */


function beginSession(token) {
  if (!token || !isTokenValid(token) || isTokenExpired(token)) {
    return null;
  }

  putCookie(COOKIE_KEY, cleanToken(token), COOKIE_OPTIONS);
  debug('Starting session.'); // Get a reference to the users new session

  var session = getSession(); // Emit an event on the window so all instances of cog can handle it.

  Events.emit(Events.LOGIN_EVENT, session); // Log some information about the session if we're debugging

  if (debug.enabled) {
    if (session.escalatedGroupExpiry) {
      debug('Using an escalated token.');
    } // Timestamps from token.


    var started = new Date(session.loggedInAt * 1000);
    var expires = new Date((session.escalatedGroupExpiry || session.exp) * 1000);
    debug({
      User: session.email,
      Started: "".concat(started.toDateString(), " ").concat(started.toLocaleTimeString()),
      Expires: "".concat(expires.toDateString(), " ").concat(expires.toLocaleTimeString())
    });
  } // Return the session since we already have it


  return session;
}
/**
 * Log the user out of their current session.
 *
 * @memberof Session
 */


function endSession() {
  deleteCookie(COOKIE_KEY, COOKIE_OPTIONS);
  debug('Session terminated.'); // Emit an event on the window so all instances of cog can handle it.

  Events.emit(Events.LOGOUT_EVENT);
}
/**
 * Extend a regular non-escalated session by refreshing the existing token.
 *
 * @memberof Session
 *
 * @return {Promise<Session~DecodedToken>}
 */


function extendSession() {
  return Request.get('/api/v1/refresh_token').then(function (_ref) {
    var loginToken = _ref.loginToken;
    return beginSession(loginToken);
  });
} // Restart the user session when the page loads in case the cookie exists but
// the session doesn't. This would happen if a new tab was opened, or the page
// was reloaded. This ensures we have the appropriate information in Sentry for
// every user.


beginSession(getSessionToken()); // =============================================================================
// ------------------------------ Event Listeners ------------------------------
// =============================================================================
// Listen to session-related events instead of handling these actions in the
// methods themselves so we can respond to other instances of cog performing
// these actions.

Events.on(Events.LOGIN_EVENT, function (session) {
  // Tell sentry about the user when they log in
  Sentry.setUserContext(session);
});
Events.on(Events.LOGOUT_EVENT, function () {
  // Clear the user information when they log out
  Sentry.setUserContext();
});
/**
 * The Session Module should be used by our applications to begin and end user
 * sessions since that enables us to automate integrations with our third-party
 * tools like Sentry as well as enable authentication for API requests.
 *
 * @type {object}
 */

var Session = {
  getSessionToken: getSessionToken,
  getSession: getSession,
  hasSession: hasSession,
  beginSession: beginSession,
  endSession: endSession,
  extendSession: extendSession
};
export default Session;